import { AppConstants } from '@ltvco/refresh-lib/ctx';
import { Stack, Text } from '@ltvco/refresh-lib/theme';
import { useReportMonitors, type ReportMonitor } from '@ltvco/refresh-lib/v1';
import {
  DeleteModal,
  generateQueryConfig,
  useBbqSearch,
  useReportChanges,
  type ReportFilter,
  type ReportType,
} from '@ltvco/refresh-lib/v2';
import {
  SmartphoneOutlined,
  PersonOutlined,
  HomeOutlined,
  EmailOutlined,
  DirectionsCarOutlined,
  AlternateEmailOutlined,
  ArrowBackIos,
  ArrowForwardIos,
} from '@mui/icons-material';
import { useContext, useMemo, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryParams } from 'use-query-params';

import {
  reportTypeLabels,
  getMonitorTitle,
} from 'components/ChatBV/components/ChatPage/ChatComponents/MonitoringColumn';
import { DEEPSEARCH_REPORT_TYPES_MAP } from 'components/ChatBV/constants';
import { ChatLayoutContext } from 'components/ChatBV/context';
import { mapReportTypeFromRecentReportsToChatType } from 'components/ChatBV/utils';
import { SearchAndFilterControls } from 'routes/Dashboard/RecentReports/components';

import { ReportCountBar, ReportLibraryBreadcrumb } from '../components';
import { ReportListItem, ReportListMetadataRow } from '../Lists/components';
import { ReportLibraryListLayout } from '../Lists/components/ListView/ReportLibraryListLayout';
import { ReportCardActions } from '../Lists/components/ReportCardActions';

import {
  MonitoredReportsList,
  PageContainer,
  PaginationButton,
  UpdatesButton,
  ViewButton,
} from './MonitoredReportsPage.styles';
import { getUnviewedChanges } from './utils';

const ITEMS_PER_PAGE = 5;
const queryConfig = generateQueryConfig();

const chatbvReportTypes = Object.values(DEEPSEARCH_REPORT_TYPES_MAP);

const reportTypeIcons: Record<string, React.ReactNode> = {
  reverse_phone_report: <SmartphoneOutlined color="primary" fontSize="small" />,
  detailed_person_report: <PersonOutlined color="primary" fontSize="small" />,
  property_report: <HomeOutlined color="primary" fontSize="small" />,
  social_network_report: <EmailOutlined color="primary" fontSize="small" />,
  username_report: <AlternateEmailOutlined color="primary" fontSize="small" />,
  vehicle_report: <DirectionsCarOutlined color="primary" fontSize="small" />,
};

const filterAndSortMonitors = (
  monitors: ReportMonitor[],
  search: string,
  reportTypes: ReportFilter[],
  sortBy: string,
  sortDirection: string
): ReportMonitor[] => {
  let result = monitors;

  if (search.trim()) {
    const query = search.trim().toLowerCase();
    result = result.filter((m) => {
      const title = getMonitorTitle(m).toLowerCase();
      const type = (
        reportTypeLabels[m.report_type] ?? m.report_type
      ).toLowerCase();
      return title.includes(query) || type.includes(query);
    });
  }

  const validTypes = reportTypes.filter(
    (t): t is ReportType => t !== 'monitored_report'
  );
  if (validTypes.length > 0) {
    result = result.filter((m) =>
      validTypes.includes(m.report_type as ReportType)
    );
  }

  result = [...result].sort((a, b) => {
    let valA: number;
    let valB: number;
    switch (sortBy) {
      case 'created_at':
        valA = Date.parse(a.created_at) || 0;
        valB = Date.parse(b.created_at) || 0;
        break;
      case 'alphabetical':
        return sortDirection === 'asc'
          ? getMonitorTitle(a).localeCompare(getMonitorTitle(b))
          : getMonitorTitle(b).localeCompare(getMonitorTitle(a));
      case 'updated_at':
      default:
        valA = Date.parse(a.updated_at) || 0;
        valB = Date.parse(b.updated_at) || 0;
        break;
    }
    return sortDirection === 'asc' ? valA - valB : valB - valA;
  });

  return result;
};

export const MonitoredReportsPage = () => {
  const navigate = useNavigate();
  const { currentMonitors, toggleReportIsMonitored } = useReportMonitors();
  const { reportState, monitoringState } = useContext(ChatLayoutContext);
  const {
    config: { reportTypeFilterOptions },
  } = useContext(AppConstants);

  const { searchBy, onSearchChange } = useBbqSearch();
  const [queryParams, setQueryParams] = useQueryParams(queryConfig);
  const { sort_by, sort_direction } = queryParams;
  const [filterTypes, setFilterTypes] = useState<ReportFilter[]>([]);

  const [page, setPage] = useState(0);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);

  const allMonitors = (currentMonitors ?? []).filter((m) =>
    chatbvReportTypes.includes(m.report_type)
  );

  const filteredReportTypeOptions = Object.keys(reportTypeFilterOptions)
    .filter((key) => (chatbvReportTypes as string[]).includes(key))
    .reduce((acc, key) => {
      acc[key] = reportTypeFilterOptions[key];
      return acc;
    }, {} as typeof reportTypeFilterOptions);

  const monitors = useMemo(
    () =>
      filterAndSortMonitors(
        allMonitors,
        searchBy,
        filterTypes,
        sort_by,
        sort_direction
      ),
    [allMonitors, searchBy, filterTypes, sort_by, sort_direction]
  );

  const totalPages = Math.max(1, Math.ceil(monitors.length / ITEMS_PER_PAGE));
  const paginatedMonitors = monitors.slice(
    page * ITEMS_PER_PAGE,
    (page + 1) * ITEMS_PER_PAGE
  );

  const handleSelectMonitor = (monitor: ReportMonitor) => {
    monitoringState.openMonitoring(monitor);
  };

  const handleOpenReportInSidebar = (monitor: ReportMonitor) => {
    const mappedType = mapReportTypeFromRecentReportsToChatType(
      monitor.report_type
    );
    if (!mappedType) {
      return;
    }
    monitoringState.closeMonitoring();
    reportState.openReportFromReportLibrary(monitor.permalink, mappedType);
  };

  const handleBack = () => {
    navigate('/chatbv/chat/library', { replace: true });
  };

  const handleDeleteAll = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    for (const monitor of allMonitors) {
      toggleReportIsMonitored({
        deleteMonitor: true,
        permalinkToDeleteParam: monitor.permalink,
      });
    }
    setDeleteAllModalOpen(false);
  };

  const handleDeleteAllModalClose = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setDeleteAllModalOpen(false);
  };

  return (
    <PageContainer>
      <Stack
        padding={2.5}
        paddingTop={1}
        width="100%"
        sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
      >
        <ReportLibraryBreadcrumb
          title="Monitored Reports"
          onBack={handleBack}
        />

        <ReportLibraryListLayout>
          <SearchAndFilterControls
            filterTypes={filterTypes}
            setFilterTypes={setFilterTypes}
            onSearchChange={onSearchChange}
            setQueryParams={setQueryParams}
            reportTypeFilterOptions={filteredReportTypeOptions}
            sort_by={sort_by}
            sort_direction={sort_direction}
            isInChatPage
          />
          <ReportCountBar
            count={monitors.length}
            label="monitored"
            showDeleteAll={monitors.length > 0}
            onDeleteAll={() => setDeleteAllModalOpen(true)}
          />
        </ReportLibraryListLayout>

        <MonitoredReportsList spacing={2}>
          {paginatedMonitors.map((monitor) => (
            <MonitoredReportCard
              key={monitor.permalink}
              monitor={monitor}
              isSelected={
                monitoringState.selectedMonitor?.permalink === monitor.permalink
              }
              onSelect={() => handleSelectMonitor(monitor)}
              onViewReport={() => handleOpenReportInSidebar(monitor)}
              onDelete={() =>
                toggleReportIsMonitored({
                  deleteMonitor: true,
                  permalinkToDeleteParam: monitor.permalink,
                })
              }
            />
          ))}

          {monitors.length === 0 && (
            <Stack alignItems="center" py={6}>
              <Text variant="body1" color="text.secondary">
                No monitored reports yet.
              </Text>
            </Stack>
          )}

          {totalPages > 1 && (
            <Stack
              direction="row"
              justifyContent="center"
              alignItems="center"
              spacing={1}
              mt={3}
            >
              <PaginationButton
                size="small"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
                aria-label="Previous page"
              >
                <ArrowBackIos sx={{ fontSize: 14 }} />
              </PaginationButton>
              <PaginationButton
                size="small"
                disabled={page >= totalPages - 1}
                onClick={() => setPage((p) => p + 1)}
                aria-label="Next page"
              >
                <ArrowForwardIos sx={{ fontSize: 14 }} />
              </PaginationButton>
            </Stack>
          )}
        </MonitoredReportsList>

        <DeleteModal
          isOpen={deleteAllModalOpen}
          setIsOpen={handleDeleteAllModalClose}
          deleteMutation={handleDeleteAll}
          title="Remove all monitored reports?"
          confirmationText="All reports will be unmonitored. You can re-enable monitoring from individual reports."
          deleteText="Remove all"
        />
      </Stack>
    </PageContainer>
  );
};

interface MonitoredReportCardProps {
  monitor: ReportMonitor;
  isSelected: boolean;
  onSelect: () => void;
  onViewReport: () => void;
  onDelete: () => void;
}

const MonitoredReportCard = ({
  monitor,
  isSelected,
  onSelect,
  onViewReport,
  onDelete,
}: MonitoredReportCardProps) => {
  const title = getMonitorTitle(monitor);
  const subtitle = reportTypeLabels[monitor.report_type] || monitor.report_type;
  const icon = reportTypeIcons[monitor.report_type] ?? (
    <PersonOutlined color="primary" fontSize="small" />
  );

  return (
    <ReportListItem
      icon={icon}
      title={title}
      subtitle={subtitle}
      metadata={
        <ReportListMetadataRow
          updated={monitor.updated_at}
          created={monitor.created_at}
          viewed={monitor.updated_at}
          middleLabel="Monitored"
        />
      }
      footer={
        <ReportCardActions
          permalink={monitor.permalink}
          reportType={monitor.report_type}
          reportTitle={title}
          deleteCurrentReport={onDelete}
        />
      }
      trailing={
        <Stack direction="row" alignItems="center" spacing={1}>
          <MonitorUpdatesButton
            permalink={monitor.permalink}
            onClick={onSelect}
          />
          <ViewButton variant="text" size="small" onClick={onViewReport}>
            View
          </ViewButton>
        </Stack>
      }
      sx={{
        borderColor: isSelected ? 'primary.main' : undefined,
        borderWidth: isSelected ? 2 : undefined,
      }}
    />
  );
};

interface MonitorUpdatesButtonProps {
  permalink: string;
  onClick: () => void;
}

const MonitorUpdatesButton = ({
  permalink,
  onClick,
}: MonitorUpdatesButtonProps) => {
  const { data: changes } = useReportChanges({ permalink });
  const unviewedCount = getUnviewedChanges(changes ?? []).length;

  if (unviewedCount === 0) {
    return null;
  }

  return (
    <UpdatesButton variant="text" size="small" onClick={onClick}>
      {unviewedCount} Update{unviewedCount !== 1 ? 's' : ''}
    </UpdatesButton>
  );
};
